// ── 신규 콜 등록 폼 (데스크톱 2줄 격자 + 모바일 스택) ────────────────────

import React from 'react';
import { PHONE_INPUT_MAX_LEN } from './constants';
import { sanitizePhoneInput } from './utils';
import {
  registrationSectionStyle, input, submitBtn,
  mobileSalesWrap, mobileFixedLabel, mobileDateFieldStyle,
  mobileDateTextStyle, mobileDateIconStyle, mobileDateNativeInputStyle,
  submitResultTextStyle,
} from './formStyles';
import {
  regWrapStyle, regGridStyle, regRowDivider, regLabelCell, regInputCell, regInput,
  regNaverInput, regContentInput, regFixedLabel, regSubmitBtn,
} from './registrationGridStyles';
import { sectionHeaderRowStyle, sectionLabelStyle } from './listStyles';

export function RegistrationForm({
  adminMode, isMobile, companyCode, isExtCategoryFixed, isMovieAdminMode,
  form, setForm, naverText, setNaverText, onPasteNaver,
  ticketTypes, currentExternalCategoryName,
  ownerOptions, fixedSalesPerson, setFixedSalesPerson,
  formContentRef, saving, onSubmit, submitResultText,
}) {
  // 상품 필드 읽기 전용 조건 — 2026-09-20 이후 기준은 하나뿐이다:
  //   **상품 관리 > "회사에 붙이기" 의 표시 방식**(isExtCategoryFixed).
  //   '카테고리 고정' 이면 분류 이름이 글자로 박히고, '상품 고르기' 면 드롭다운.
  //   붙여둔 분류가 없는 회사는 서버가 is_category=false 를 주므로 드롭다운이 된다.
  //   (예전에는 직원 신분이 external 인지로 갈랐다 — 회사마다 다르게 쓸 수가 없었다.)
  const isTicketTypeReadOnly =
    (!adminMode && !companyCode && isExtCategoryFixed) ||
    (Boolean(companyCode) && isExtCategoryFixed) ||
    isMovieAdminMode;

  const ticketTypeField = isTicketTypeReadOnly ? (
    <input style={{ ...regInput, background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}
      value={currentExternalCategoryName} readOnly />
  ) : (
    // value: ticket_types.id (정수), onChange: ticket_type_id에 숫자 저장
    <select style={regInput} value={form.ticket_type_id || ''}
      onChange={(e) => setForm({ ...form, ticket_type_id: Number(e.target.value) || null })}>
      <option value="">상품 선택</option>
      {ticketTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );

  const ticketTypeFieldMobile = isTicketTypeReadOnly ? (
    <input style={{ ...input, background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}
      value={currentExternalCategoryName} readOnly />
  ) : (
    <select style={input} value={form.ticket_type_id || ''}
      onChange={(e) => setForm({ ...form, ticket_type_id: Number(e.target.value) || null })}>
      <option value="">상품 선택</option>
      {ticketTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );

  return (
    <section style={registrationSectionStyle(isMobile)}>
      {/* 직원 전용 폼 섹션 헤더 */}
      {!adminMode && (
        <div style={sectionHeaderRowStyle}>
          <span style={sectionLabelStyle}>신규 콜 등록</span>
          {submitResultText && <span style={submitResultTextStyle}>{submitResultText}</span>}
        </div>
      )}

      {/* ── 데스크톱 2줄 격자 폼 (2026-09-15 시안: 검정 라벨 칸 + 흰 입력 칸) ── */}
      {!isMobile && (
        <div style={regWrapStyle}>
          <div style={regGridStyle}>
            {/* 1줄: 담당자 · 상품 · 방문일자 · 미팅시간 · 내용 · 마케팅 담당자 · 등록 */}
            {adminMode && (
              <>
                {/* 담당자 이름만 — 아이디·번호 괄호는 빼달라는 요청(2026-10-15). PC·휴대폰 두 곳 같음 */}
                <div style={regLabelCell(1, '1')}>담당자</div>
                <div style={regInputCell(1, '2')}>
                  <select style={regInput} value={form.owner_user_id}
                    onChange={(e) => setForm({ ...form, owner_user_id: e.target.value })}>
                    <option value="">담당자 선택</option>
                    {ownerOptions.map((o) => (
                      <option key={o.id} value={String(o.id)}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {/* 직원 화면엔 담당자 칸이 없어 상품이 그 자리까지 넓어진다 — 나머지 칸 위치는 그대로 */}
            <div style={regLabelCell(1, adminMode ? '3' : '1')}>상품</div>
            <div style={regInputCell(1, adminMode ? '4' : '2 / 5')}>{ticketTypeField}</div>
            <div style={regLabelCell(1, '5')}>방문일자</div>
            <div style={regInputCell(1, '6')}>
              <input style={regInput} type="date" value={form.visit_date}
                onChange={(e) => setForm({ ...form, visit_date: e.target.value })} />
            </div>
            <div style={regLabelCell(1, '7')}>미팅시간</div>
            <div style={regInputCell(1, '8')}>
              <input style={regInput} value={form.visit_time} onChange={(e) => setForm({ ...form, visit_time: e.target.value })} />
            </div>
            <div style={regLabelCell(1, '9')}>내용</div>
            {/* 내용: textarea — 타이핑·붙여넣기 시 높이 자동 확장 (useEffect 처리) */}
            <div style={regInputCell(1, '10 / 13')}>
              <textarea ref={formContentRef} style={regContentInput} rows={1} value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })} />
            </div>
            <div style={regLabelCell(1, '13')}>마케팅 담당자</div>
            <div style={regInputCell(1, '14')}>
              <input style={regInput} value={form.sales_person_name}
                onChange={(e) => setForm({ ...form, sales_person_name: e.target.value })} />
              <label style={regFixedLabel}>
                <input type="checkbox" checked={fixedSalesPerson}
                  onChange={(e) => setFixedSalesPerson(e.target.checked)} />
                고정
              </label>
            </div>
            <div style={regLabelCell(1, '15')}>등록</div>
            <div style={{ ...regInputCell(1, '16'), justifyContent: 'center', flexWrap: 'wrap' }}>
              <button style={regSubmitBtn} disabled={saving} onClick={onSubmit}>
                {saving ? '등록중...' : '등록하기'}
              </button>
              {adminMode && submitResultText && <span style={submitResultTextStyle}>{submitResultText}</span>}
            </div>

            <div style={regRowDivider} />

            {/* 2줄: 네이버 플레이스 · 상호 · 업종 · 주소 · 전화번호 · 휴대폰 · 대표자 */}
            <div style={regLabelCell(2, '1')}>네이버 플레이스</div>
            <div style={regInputCell(2, '2')}>
              <textarea style={regNaverInput} value={naverText}
                onChange={(e) => setNaverText(e.target.value)}
                onPaste={onPasteNaver} placeholder="Ctrl + V" />
            </div>
            <div style={regLabelCell(2, '3')}>상호</div>
            <div style={regInputCell(2, '4')}>
              <input style={regInput} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </div>
            <div style={regLabelCell(2, '5')}>업종</div>
            <div style={regInputCell(2, '6')}>
              <input style={regInput} value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
            </div>
            <div style={regLabelCell(2, '7')}>주소</div>
            <div style={regInputCell(2, '8 / 11')}>
              <input style={regInput} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div style={regLabelCell(2, '11')}>전화번호</div>
            <div style={regInputCell(2, '12')}>
              <input style={regInput} value={form.phone} maxLength={PHONE_INPUT_MAX_LEN}
                inputMode="tel" pattern="[0-9-]*"
                onChange={(e) => setForm({ ...form, phone: sanitizePhoneInput(e.target.value) })} />
            </div>
            <div style={regLabelCell(2, '13')}>휴대폰</div>
            <div style={regInputCell(2, '14')}>
              <input style={regInput} value={form.mobile_phone} maxLength={PHONE_INPUT_MAX_LEN}
                inputMode="tel" pattern="[0-9-]*"
                onChange={(e) => setForm({ ...form, mobile_phone: sanitizePhoneInput(e.target.value) })} />
            </div>
            <div style={regLabelCell(2, '15')}>대표자</div>
            <div style={regInputCell(2, '16')}>
              <input style={regInput} value={form.ceo_name} onChange={(e) => setForm({ ...form, ceo_name: e.target.value })} />
            </div>
          </div>
        </div>
      )}

      {/* ── 모바일 스택 폼 ── */}
      {isMobile && (
        <div style={{ display: 'grid', gap: 8 }}>
          {adminMode && (
            <select style={input} value={form.owner_user_id}
              onChange={(e) => setForm({ ...form, owner_user_id: e.target.value })}>
              <option value="">담당자 선택</option>
              {ownerOptions.map((o) => (
                <option key={o.id} value={String(o.id)}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
          <textarea style={input} value={naverText}
            onChange={(e) => setNaverText(e.target.value)}
            onPaste={onPasteNaver} placeholder="Ctrl + V" />
          {ticketTypeFieldMobile}
          {/* 날짜 커스텀 UI + 숨겨진 네이티브 input */}
          <div style={mobileDateFieldStyle}>
            <span style={{ ...mobileDateTextStyle, color: form.visit_date ? 'var(--text)' : 'var(--text-muted)' }}>
              {form.visit_date || '방문일자 선택'}
            </span>
            <span style={mobileDateIconStyle}>📅</span>
            <input style={mobileDateNativeInputStyle} type="date" aria-label="방문일자"
              value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} />
          </div>
          <input style={input} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="상호" />
          <input style={input} value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="업종" />
          <input style={input} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="주소" />
          <input style={input} value={form.visit_time} onChange={(e) => setForm({ ...form, visit_time: e.target.value })} placeholder="미팅시간" />
          <input style={input} value={form.phone} maxLength={PHONE_INPUT_MAX_LEN} inputMode="tel" pattern="[0-9-]*"
            onChange={(e) => setForm({ ...form, phone: sanitizePhoneInput(e.target.value) })} placeholder="전화번호" />
          <input style={input} value={form.mobile_phone} maxLength={PHONE_INPUT_MAX_LEN} inputMode="tel" pattern="[0-9-]*"
            onChange={(e) => setForm({ ...form, mobile_phone: sanitizePhoneInput(e.target.value) })} placeholder="휴대폰" />
          <input style={input} value={form.ceo_name} onChange={(e) => setForm({ ...form, ceo_name: e.target.value })} placeholder="대표자" />
          <textarea style={input} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="내용" />
          <div style={mobileSalesWrap}>
            <input style={input} value={form.sales_person_name}
              onChange={(e) => setForm({ ...form, sales_person_name: e.target.value })} placeholder="마케팅담당자" />
            <label style={mobileFixedLabel}>
              <input type="checkbox" checked={fixedSalesPerson} onChange={(e) => setFixedSalesPerson(e.target.checked)} />
              마케팅담당자 고정
            </label>
          </div>
          <button style={submitBtn} onClick={onSubmit} disabled={saving}>
            {saving ? '등록 중...' : '등록하기'}
          </button>
        </div>
      )}
    </section>
  );
}
